import { Component, Input } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';
import { GlobalReferenceService, ReferenceImportMode } from 'src/app/services/global-reference.service';

export type ReferenceImportKind = 'dsk' | 'vendor';

@Component({
  selector: 'app-reference-import-dialog',
  templateUrl: './reference-import-dialog.component.html',
  styleUrls: ['./reference-import-dialog.component.scss']
})
export class ReferenceImportDialogComponent {
  @Input() kind: ReferenceImportKind = 'dsk';

  selectedFile: File | null = null;
  importMode: ReferenceImportMode = 'merge';
  importing = false;
  result: any = null;
  errorMsg = '';

  constructor(
    private dialogRef: NbDialogRef<ReferenceImportDialogComponent>,
    private globalReferenceService: GlobalReferenceService,
    private toastr: ToastrService
  ) {}

  get title(): string {
    return this.kind === 'dsk' ? 'Import DSK Groups from CSV' : 'Import Vendors from CSV';
  }

  get replaceWarning(): string {
    return this.kind === 'dsk'
      ? 'This deletes all existing DSK groups before import.'
      : 'This deletes all existing vendor records before import.';
  }

  downloadTemplate(): void {
    const request$ = this.kind === 'dsk'
      ? this.globalReferenceService.downloadDskGroupsTemplate()
      : this.globalReferenceService.downloadVendorsTemplate();

    request$.subscribe({
      error: (err: any) => {
        const message = err?.error?.response?.message || err?.message || 'Template download failed';
        this.errorMsg = message;
        this.toastr.error(message);
      }
    });
  }

  onFileSelect(event: any): void {
    const file: File = event.target.files[0];
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.errorMsg = 'Please select a CSV file (.csv)';
      this.selectedFile = null;
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.errorMsg = 'File must be under 10MB';
      this.selectedFile = null;
      return;
    }
    this.selectedFile = file;
    this.errorMsg = '';
    this.result = null;
  }

  import(): void {
    if (!this.selectedFile) {
      return;
    }

    const runImport = () => {
      this.importing = true;
      this.errorMsg = '';
      const formData = new FormData();
      formData.append('csvFile', this.selectedFile as File);

      const request$ = this.kind === 'dsk'
        ? this.globalReferenceService.importDskGroups(formData, this.importMode)
        : this.globalReferenceService.importVendors(formData, this.importMode);

      request$.subscribe({
        next: (res: any) => {
          this.importing = false;
          this.result = res?.response?.data ?? res?.data ?? res;
        },
        error: (err: any) => {
          this.importing = false;
          this.errorMsg = err?.error?.response?.message || 'Import failed. Please try again.';
        }
      });
    };

    if (this.importMode === 'replace') {
      Swal.fire({
        title: 'Replace all existing records?',
        text: this.replaceWarning,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, replace all',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#dc3545'
      }).then((result) => {
        if (result.isConfirmed) {
          runImport();
        }
      });
      return;
    }

    runImport();
  }

  close(): void {
    this.dialogRef.close(this.result !== null);
  }
}
